import { Injectable, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CompanySessionService } from '@core/auth/company-session.service';

/**
 * Whether the active company's profile still needs filling in, and navigation to
 * the profile edit form. Mirrors {@link AccountManagerAgentService}: every surface
 * that nudges for a complete profile (dashboard checklist, hire-request header,
 * ...) shares one completeness rule and one destination route.
 */
@Injectable({ providedIn: 'root' })
export class CompanyProfileSetupService {
  private readonly companySession = inject(CompanySessionService);
  private readonly router = inject(Router);

  /** super_admin only — matches the company PATCH endpoint's role requirement. */
  private readonly canEdit = computed(() => this.companySession.activeMembership()?.role === 'super_admin');

  /** The profile counts as set up once name, description and sector are all on file. */
  readonly isComplete = computed(() => {
    const company = this.companySession.activeCompanyDetails();
    return !!(company?.name && company?.about && company?.industry);
  });

  readonly showBanner = computed(() => this.canEdit() && !this.isComplete());

  /**
   * Navigate to the company profile, opened straight into its edit form.
   * `returnUrl`, when given, is carried as a query param so the profile page's
   * save/cancel actions can return the caller to wherever it launched from.
   */
  goToSetup(returnUrl?: string): void {
    this.router.navigate(['/settings/company-profile'], {
      queryParams: returnUrl ? { edit: '1', returnUrl } : { edit: '1' },
    });
  }
}
